import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Home from "./pages/Home";
import InsightsIndex from "./pages/InsightsIndex";
import InsightDetail from "./pages/InsightDetail";
import { pathLocale, pathInOtherLocale, withSpanishPrefix, stripLocalePrefix } from "./i18n/useT";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

/** Keeps <html lang> and the hreflang link tags in sync with the current route. */
function LocaleHead() {
  const { pathname } = useLocation();
  const locale = pathLocale(pathname);

  useEffect(() => {
    document.documentElement.lang = locale === "es" ? "es" : "en";

    const head = document.head;
    const origin = window.location.origin;

    const ensureLink = (rel: string, hreflang: string | null, href: string) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let el = head.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        if (hreflang) el.setAttribute("hreflang", hreflang);
        head.appendChild(el);
      }
      el.href = href;
    };

    const enPath = stripLocalePrefix(pathname) || "/";
    const esPath = pathname === "/" ? "/es" : pathInOtherLocale(pathname);
    const canonical = locale === "es" ? `${origin}${esPath}` : `${origin}${enPath}`;

    ensureLink("canonical", null, canonical);
    ensureLink("alternate", "en", `${origin}${enPath}`);
    ensureLink(
      "alternate",
      "es",
      `${origin}${pathname === "/" ? "/es" : withSpanishPrefix(stripLocalePrefix(pathname))}`,
    );
    ensureLink("alternate", "x-default", `${origin}${enPath}`);
  }, [pathname, locale]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <LocaleHead />
      <ScrollManager />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/insights" element={<InsightsIndex />} />
        <Route path="/insights/:slug" element={<InsightDetail />} />
        <Route path="/es" element={<Home />} />
        <Route path="/es/insights" element={<InsightsIndex />} />
        <Route path="/es/insights/:slug" element={<InsightDetail />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}
